/**
 * Patient-facing state coverage guidance.
 *
 * The Dashboard API is the source of truth when it has a complete mandate
 * record. This reference fills in the treatment categories the API does not
 * currently expose (for example, preservation, embryo storage, and donor
 * services), so a patient does not see "IVF" as a misleading shorthand for a
 * much broader state rule.
 *
 * Primary reference: KFF State Health Facts, "Mandated Coverage of
 * Infertility Treatment," as of November 2025, checked in July 2026. State
 * statutes and regulator guidance are used where a state publishes more
 * useful service-level detail or has changed since that table was compiled.
 */

export const STATE_COVERAGE_REFERENCE = {
  label: "KFF state reference, November 2025",
  url: "https://www.kff.org/state-health-policy-data/state-indicator/infertility-coverage/",
  reviewedAt: "July 2026",
};

export type ResolvedStateCoverageGuidance = {
  coverageType: string;
  whoCovered: string;
  included: string;
  requirements: string;
  sourceLabel: string;
  sourceUrl: string;
};

const KFF_SOURCE = STATE_COVERAGE_REFERENCE.url;
const STATE_LAW_SOURCE =
  "https://integration.reproductivefacts.org/patient-advocacy/state-and-territory-infertility-insurance-laws/";

const NO_PRIVATE_MANDATE_STATES = new Set([
  "AL",
  "AK",
  "AZ",
  "FL",
  "ID",
  "IN",
  "IA",
  "KS",
  "MI",
  "MN",
  "MS",
  "MO",
  "NE",
  "NV",
  "NM",
  "NC",
  "ND",
  "OR",
  "PA",
  "SC",
  "SD",
  "TN",
  "VT",
  "VA",
  "WA",
  "WI",
  "WY",
]);

type StateCoverageRecord = Omit<ResolvedStateCoverageGuidance, "sourceUrl"> & {
  sourceUrl?: string;
};

const KFF_RECORD = (record: Omit<StateCoverageRecord, "sourceLabel">) => ({
  ...record,
  sourceLabel: "KFF state table, November 2025",
});

/**
 * State-level private insurance rules. These are intentionally specific about
 * scope and service type: "covered" does not mean every plan or every
 * fertility service is covered.
 */
const STATE_COVERAGE_GUIDANCE: Record<string, StateCoverageRecord> = {
  AR: KFF_RECORD({
    coverageType: "Mandate to cover",
    whoCovered: "Individual and group insurers; HMOs may be excluded",
    included: "IVF, including cryopreservation as part of the IVF procedure",
    requirements:
      "A $15,000 lifetime minimum is listed. Coverage generally requires the policyholder and spouse's sperm, 2 years of unexplained infertility or a listed medical cause, and trying less costly treatment first. No general fertility-preservation mandate is listed.",
    sourceUrl: `${STATE_LAW_SOURCE}arkansas/`,
  }),
  CA: {
    coverageType: "Mandate to cover",
    whoCovered:
      "Large-group plans must cover; small-group plans must offer. Self-funded and religious plans may be excluded.",
    included:
      "Diagnosis and screening, reproductive counseling, IVF, donor and surrogate services, and cryopreservation/storage of sperm, eggs, and embryos. Medical fertility preservation may also apply when treatment could cause infertility.",
    requirements:
      "For eligible large-group plans, the law includes up to 3 completed oocyte retrievals with unlimited embryo transfers. Small-group plans must offer the benefit, but do not have to elect it. CalPERS timing differs.",
    sourceLabel: "California DMHC and Insurance Code §10119.6",
    sourceUrl:
      "https://www.dmhc.ca.gov/Resources/Newsroom/Newsletters/Spring2026Newsletter.aspx",
  },
  CO: KFF_RECORD({
    coverageType: "Mandate to cover",
    whoCovered:
      "Large-group plans; individual and small-group plans are excluded",
    included:
      "Infertility diagnosis and treatment, plus standard fertility preservation for treatment-related infertility",
    requirements:
      "The large-group rule includes up to 3 completed oocyte retrievals and unlimited embryo transfers under ASRM guidelines. Self-funded and religious plans may be excluded; confirm whether your plan is fully insured and subject to Colorado law.",
    sourceUrl: "https://leg.colorado.gov/bills/HB20-1158",
  }),
  CT: KFF_RECORD({
    coverageType: "Mandate to cover",
    whoCovered:
      "Eligible health insurance organizations; self-funded and religious plans may be excluded",
    included:
      "Infertility diagnosis, testing, medications, IUI, IVF, and medical fertility preservation",
    requirements:
      "People enrolled for less than 12 months may be excluded. The treatment rule lists up to 4 ovulation-induction cycles and 3 IUI cycles; check the plan for authorization and network rules.",
    sourceUrl: `${STATE_LAW_SOURCE}connecticut/`,
  }),
  DE: KFF_RECORD({
    coverageType: "Mandate to cover",
    whoCovered:
      "Individual and group insurers, including HMOs; employers under 50 may be excluded",
    included:
      "Consultation, diagnostics, medication, IUI, IVF, other infertility treatment, and medical fertility preservation",
    requirements:
      "The rule excludes egg retrieval after age 45 or after 6 retrievals, compensation for surrogacy, and reversal of voluntary sterilization. Self-funded and religious plans may be excluded.",
    sourceUrl: `${STATE_LAW_SOURCE}delaware/`,
  }),
  DC: {
    coverageType: "Mandate to cover",
    whoCovered:
      "Individual, small-group, and large-group plans; self-funded and limited-benefit plans are excluded",
    included:
      "Infertility diagnosis and treatment, IVF, fertility medication, standard fertility preservation (including egg freezing), and embryo-transfer costs involving a third party",
    requirements:
      "At least 3 complete oocyte retrievals and unlimited embryo transfers are listed. Coverage follows the physician's plan of care and cannot be limited differently from comparable care; surrogate care after embryo transfer is excluded.",
    sourceLabel: "D.C. Law 25-49 and DISB 2025 guide",
    sourceUrl:
      "https://code.dccouncil.gov/us/dc/council/code/sections/31-3834.06",
  },
  GA: KFF_RECORD({
    coverageType: "Mandate to cover",
    whoCovered: "Eligible insured plans; self-funded plans may be excluded",
    included:
      "Standard fertility preservation when medically necessary treatment may cause infertility",
    requirements:
      "Georgia's 2026 rule is preservation-focused, not a broad IVF mandate. It applies to qualifying policies issued or renewed after January 1, 2026; ask whether your treatment meets the medical-necessity rule.",
    sourceUrl:
      "https://gov.georgia.gov/document/2025-signed-legislation/hb-94/download",
  }),
  HI: KFF_RECORD({
    coverageType: "Mandate to cover",
    whoCovered:
      "Individual and group insurers; self-funded plans may be excluded",
    included: "One IVF cycle and related outpatient IVF services",
    requirements:
      "Eligibility historically includes 5 years of unexplained infertility or a listed medical cause such as male factor, endometriosis, or tubal disease. Confirm the plan's age, spouse/gamete, authorization, and network rules.",
    sourceUrl: `${STATE_LAW_SOURCE}hawaii/`,
  }),
  IL: KFF_RECORD({
    coverageType: "Mandate to cover",
    whoCovered:
      "Group insurers and HMOs; individual and group plans for treatment-related infertility",
    included:
      "Infertility diagnosis, testing, IUI, IVF, other treatment, and medical fertility preservation",
    requirements:
      "Employers under 25, self-funded plans, and religious employers may be excluded. The treatment rule lists more than 6 egg retrievals as excluded and may require 1 year of infertility or a qualifying medical treatment.",
    sourceUrl: `${STATE_LAW_SOURCE}illinois/`,
  }),
  KY: {
    coverageType: "Mandate to cover",
    whoCovered:
      "Health benefit plans; religious employer plans may be excluded",
    included:
      "Evaluation, laboratory testing, medication, and oocyte or sperm freezing/storage when medical treatment may cause infertility",
    requirements:
      "Kentucky's current law guarantees medical fertility preservation, including up to 1 year of gamete storage. A 2026 IVF expansion bill is still pending, so this should not be presented as a current broad IVF mandate.",
    sourceLabel: "Kentucky Revised Statute §304.17A-261",
    sourceUrl:
      "https://apps.legislature.ky.gov/law/statutes/statute.aspx?id=54272",
  },
  LA: {
    coverageType: "Mandate to cover",
    whoCovered:
      "Eligible health coverage plans; religious employers may request an exemption",
    included:
      "Standard fertility preservation, including oocyte and sperm preservation, when medically necessary treatment may cause infertility",
    requirements:
      "This is not a broad IVF mandate. It applies to iatrogenic infertility, including cancer treatment; storage after 3 years may be excluded and normal cost-sharing or limits may apply.",
    sourceLabel: "Louisiana Revised Statute §22:1036.1",
    sourceUrl: "https://www.legis.la.gov/legis/Law.aspx?d=1337005",
  },
  ME: {
    coverageType: "Mandate to cover",
    whoCovered: "Eligible health insurance organizations",
    included:
      "Fertility diagnosis and treatment, medications, IVF/IUI, and preservation of gametes, embryos, and reproductive material",
    requirements:
      "Maine defines a fertility patient broadly, including people with infertility, genetic risk, or without necessary gametes. Preservation includes up to 5 years of storage; plan cost-sharing and clinical rules still apply.",
    sourceLabel: "Maine Title 24-A §4320-U",
    sourceUrl:
      "https://legislature.maine.gov/legis/statutes/24-A/title24-Asec4320-U.html",
  },
  MD: KFF_RECORD({
    coverageType: "Mandate to cover",
    whoCovered:
      "Eligible individual and group insurers; employers under 50 may be excluded",
    included:
      "Infertility diagnosis and treatment, including IVF, plus medical fertility preservation",
    requirements:
      "The treatment rule lists up to 3 IVF cycles per live birth and a $100,000 lifetime maximum. Self-funded and religious plans may be excluded; verify whether storage and medications are included in your plan.",
    sourceUrl: `${STATE_LAW_SOURCE}maryland/`,
  }),
  MA: {
    coverageType: "Mandate to cover",
    whoCovered: "Fully insured plans from Massachusetts insurers and HMOs",
    included:
      "Infertility diagnosis, treatment, medication, IUI, IVF, and standard fertility preservation, including procurement, freezing, and storage of gametes or embryos when medically indicated",
    requirements:
      "Self-funded employer plans are excluded. Fertility preservation applies when a medical or genetic condition or treatment may impair fertility; plan cost-sharing and clinical criteria still apply.",
    sourceLabel: "Massachusetts DOI fertility-preservation guidance",
    sourceUrl:
      "https://www.mass.gov/info-details/frequently-asked-questions-about-fertility-preservation-services",
  },
  MT: KFF_RECORD({
    coverageType: "Mandate to cover",
    whoCovered:
      "HMOs; some individual and group plans for cancer-related infertility",
    included:
      "HMO infertility services and fertility preservation for cancer-related iatrogenic infertility",
    requirements:
      "Montana's HMO mandate does not define every infertility service, and self-funded or religious plans may be excluded. Ask the HMO whether IVF, IUI, medications, and storage are included.",
    sourceUrl: `${STATE_LAW_SOURCE}montana/`,
  }),
  NH: KFF_RECORD({
    coverageType: "Mandate to cover",
    whoCovered:
      "Eligible group insurers; some small-business/ACA transition plans may be excluded",
    included:
      "Infertility diagnosis, treatment, medications, egg/sperm procurement, and medical fertility preservation",
    requirements:
      "Coverage is tied to impaired ability to become or cause pregnancy. Reversal of voluntary sterilization and some surrogate-related care are excluded; self-funded plans are excluded.",
    sourceUrl: `${STATE_LAW_SOURCE}new-hampshire/`,
  }),
  NJ: KFF_RECORD({
    coverageType: "Mandate to cover",
    whoCovered:
      "Group insurers, HMOs, and state/school employee plans; employers under 50 may be excluded",
    included:
      "Infertility diagnosis, medication, surgery, IUI, IVF, donor-egg and gestational-carrier services, genetic testing, and embryo transfer",
    requirements:
      "Up to 4 completed egg retrievals and unlimited embryo transfers are listed. The mandate does not require cryopreservation/storage or reversal of voluntary sterilization; self-funded and religious plans may be excluded.",
    sourceUrl: "https://www.nj.gov/dobi/pn02_260.htm",
  }),
  NY: KFF_RECORD({
    coverageType: "Mandate to cover",
    whoCovered:
      "Large-group plans for IVF; all commercial markets for cryopreservation",
    included:
      "Infertility visits, testing, medications, 3 IVF cycles, and standard preservation for treatment-related infertility",
    requirements:
      "IVF generally applies to employers over 100 employees; individual and small-group plans are excluded for IVF. Preservation applies to iatrogenic infertility, with plan deductible/coinsurance; self-funded plans are excluded.",
    sourceUrl: `${STATE_LAW_SOURCE}new-york/`,
  }),
  OH: KFF_RECORD({
    coverageType: "Mandate to cover",
    whoCovered: "HMOs only",
    included:
      "Diagnostic and exploratory procedures to identify or correct an infertility cause",
    requirements:
      "Ohio's mandate does not require HMOs to cover IVF or other infertility treatment, and it does not apply to other insurer types. Ask the plan what treatment benefits it voluntarily includes.",
    sourceUrl: `${STATE_LAW_SOURCE}ohio/`,
  }),
  OK: {
    coverageType: "Mandate to cover",
    whoCovered: "Eligible health insurance organizations",
    included:
      "Fertility preservation when cancer treatment may directly or indirectly cause infertility",
    requirements:
      "This is not a broad IVF mandate. Preservation is tied to medically necessary cancer treatment; religious employers may be excluded and plan limits should be confirmed.",
    sourceLabel: "KFF state table, November 2025",
    sourceUrl: KFF_SOURCE,
  },
  RI: KFF_RECORD({
    coverageType: "Mandate to cover",
    whoCovered: "Insurers and HMOs; self-funded plans are excluded",
    included:
      "Infertility diagnosis and treatment, including IVF, plus medical fertility preservation",
    requirements:
      "The state summary lists ages 25–42 unless preservation, up to a 20% copay, and a $100,000 treatment maximum. A qualifying diagnosis or iatrogenic infertility may be required.",
    sourceUrl: `${STATE_LAW_SOURCE}rhode-island/`,
  }),
  TX: KFF_RECORD({
    coverageType: "Mandate to offer",
    whoCovered:
      "Employer plans that elect the offered IVF benefit; self-funded and religious plans may be excluded",
    included:
      "IVF may be offered; cancer-related fertility preservation is covered",
    requirements:
      "Texas does not automatically include IVF in every plan. Ask HR whether the employer elected the benefit and confirm medical, spouse/gamete, clinic, and prior-treatment requirements.",
    sourceUrl: `${STATE_LAW_SOURCE}texas/`,
  }),
  UT: KFF_RECORD({
    coverageType: "Mandate to cover",
    whoCovered: "Eligible insurers; self-funded plans may be excluded",
    included:
      "Fertility services under eligible insurer plans; specific treatment coverage is plan-defined",
    requirements:
      "This is not a guarantee of every IVF service. Ask whether your plan covers testing, medication, retrieval, embryo transfer, storage, or preservation for a qualifying medical condition.",
    sourceUrl: `${STATE_LAW_SOURCE}utah/`,
  }),
  WV: KFF_RECORD({
    coverageType: "Mandate to cover",
    whoCovered: "HMOs only; other insurers are excluded",
    included:
      "Infertility services, with the exact treatment list set by the HMO",
    requirements:
      "West Virginia's statute does not define every infertility service. Ask the HMO whether diagnosis, medications, IUI, IVF, preservation, or storage are included.",
    sourceUrl: `${STATE_LAW_SOURCE}west-virginia/`,
  }),
};

function noPrivateMandateGuidance(): ResolvedStateCoverageGuidance {
  return {
    coverageType: "No statewide mandate",
    whoCovered:
      "State law does not require private plans to cover fertility treatment",
    included:
      "No state-set treatment list; an employer plan may still cover testing, medication, IUI, IVF, preservation, donor, or surrogacy services",
    requirements:
      "Check the Summary of Benefits and Coverage and ask whether the plan is fully insured or self-funded before assuming a service is excluded.",
    sourceLabel: "KFF state table, November 2025",
    sourceUrl: KFF_SOURCE,
  };
}

export function getStateCoverageGuidance(
  stateCode: string,
  stateName: string,
): ResolvedStateCoverageGuidance {
  const normalizedCode = stateCode.toUpperCase();
  const record = STATE_COVERAGE_GUIDANCE[normalizedCode];

  if (record) {
    return {
      ...record,
      sourceUrl: record.sourceUrl ?? KFF_SOURCE,
    };
  }

  if (NO_PRIVATE_MANDATE_STATES.has(normalizedCode)) {
    return noPrivateMandateGuidance();
  }

  return {
    coverageType: "State-specific rule",
    whoCovered: `Plan eligibility varies in ${stateName}`,
    included: "Ask your insurer what fertility care is covered",
    requirements: "Confirm the details before you pay or submit a claim.",
    sourceLabel: STATE_COVERAGE_REFERENCE.label,
    sourceUrl: KFF_SOURCE,
  };
}

export function hasStatePrivateMandate(stateCode: string): boolean {
  return Boolean(STATE_COVERAGE_GUIDANCE[stateCode.toUpperCase()]);
}
